import { journeyModeOf, type JourneyMode } from "./dto";

// F148 (Pavel 2026-08-13, Slack go-live thread): "Jinak to udelejme pres ty
// metadata jak navrhuje Eliska." The configuration of a purchase moves ONTO
// the Stripe product, where the person who owns the products (Eliska) can set
// it, instead of living in our admin mapping table where a second person has
// to mirror every product they create.
//
// This module is the ONE reading of that metadata, shared by the Worker and
// the console, because a second copy is how the two drift (the F116 law about
// `preferredLocale`, one contract over).
//
// The keys are a CONTRACT with the platform side. They are string:string, they
// sit on the PRODUCT and not on the price, and they are named here once:
//
//   launchpad           "1"                       opt-in; nothing else is read without it
//   launchpad_label     text                      what the client bought
//   launchpad_language  cs | en                   clients.market_language
//   launchpad_app       1 | 0                     clients.includes_app
//   launchpad_source    direct | upsell | webinar clients.purchase_source
//   launchpad_start     new | existing            clients.instance_start_mode
//   launchpad_journey   full | sales_only         clients.journey_mode (optional)
//   launchpad_payments  1 | 0                     clients.includes_payments_setup (optional, F171)
//
// 🔴 The validation MIRRORS the CHECK constraints of `clients` and
// `stripe_product_mappings`. A value this module lets through and the database
// then refuses would fail INSIDE the creation batch, where the buyer's answer
// is a generic `create_state_conflict` and nobody can see which field was
// wrong. So the value set is stated here, next to the sentence that explains
// it, and the guard asserts it against the migrations.

// F153 (Slack go-live thread, Eliška 2026-08-14): "Použijeme metadata
// `productType: start` a `productType: empire`. Pokud jsou na zakoupeným
// produktu, pošleš mail s registračním odkazem pro Launchpad." Plus her own
// clarification: "jazyk se řídí podle metadata `language:` en/cs, aplikace se
// řídí podle metadat `enableMobileApp:` true, oba jedou celou cestu (příprava
// Buddyho i prodej), ne jen prodejní část."
//
// So the SAME product carries a SECOND dialect, and it is deliberately shorter:
// three keys the person selling the packages already thinks in, instead of
// seven fields describing our data model. An ARCHETYPE expands into the same
// config the launchpad_* dialect states field by field.
//
//   productType      start | empire       opt-in + archetype
//   language         cs | en              clients.market_language
//   enableMobileApp  true | false | 1 | 0 clients.includes_app (absent = false)
//
// The launchpad_* dialect below is UNCHANGED by F153. Both are read by
// `readProductMetadata`, which is the one function the Worker calls.

export const LAUNCHPAD_METADATA_FLAG = "launchpad";
export const LAUNCHPAD_METADATA_KEYS = {
  flag: LAUNCHPAD_METADATA_FLAG,
  label: "launchpad_label",
  language: "launchpad_language",
  app: "launchpad_app",
  source: "launchpad_source",
  start: "launchpad_start",
  journey: "launchpad_journey",
  // F171 (Tomas Pohl 2026-10-06): "payment setup by us included". Optional:
  // absent answers NO, the only safe direction (the step it draws is work the
  // team owes; a client who did not buy it must not see it promised).
  payments: "launchpad_payments"
} as const;

/** Mirrors `stripe_product_mappings.label` (NOT NULL) and its route's own rule. */
export const LAUNCHPAD_LABEL_MAX = 120;

export const LAUNCHPAD_LANGUAGES = ["cs", "en"] as const;
export const LAUNCHPAD_SOURCES = ["direct", "upsell", "webinar"] as const;
export const LAUNCHPAD_START_MODES = ["new", "existing"] as const;

/**
 * The issue codes a broken product configuration produces. They travel on the
 * event's `detail` (a code SET since F148) and each one has its own sentence in
 * both locales: an operator who mistyped `launchpad_language` must read which
 * field is wrong, never "import se nepodařilo" (S2-07 - no internal code and no
 * nameless refusal reaches the screen).
 */
export const METADATA_ISSUE_CODES = [
  "metadata_flag",
  "metadata_label",
  "metadata_language",
  "metadata_app",
  "metadata_source",
  "metadata_start"
] as const;
export type MetadataIssueCode = (typeof METADATA_ISSUE_CODES)[number];

/**
 * F171: the optional `launchpad_payments` key has its own issue list for the
 * F153 reason: `METADATA_ISSUE_CODES` is the set a fully broken launchpad_*
 * product owes, and its guard asserts exactly that set. A product that never
 * wrote the optional key cannot owe its issue.
 */
export const PAYMENTS_ISSUE_CODES = ["metadata_payments"] as const;
export type PaymentsIssueCode = (typeof PAYMENTS_ISSUE_CODES)[number];

export interface StripeProductConfig {
  label: string;
  marketLanguage: "cs" | "en";
  includesApp: boolean;
  purchaseSource: "direct" | "upsell" | "webinar";
  instanceStartMode: "new" | "existing";
  journeyMode: JourneyMode;
  /**
   * F153: WHICH package was bought, when the product said so in the
   * `productType` dialect. The launchpad_* dialect has no such key and omits
   * this field entirely - it describes the client's configuration, not the
   * shelf the purchase came off. The one thing that reads it is the welcome
   * mail (an Empire client gets David's personal number).
   */
  productArchetype?: ProductArchetype;
  /**
   * F171: the team sets up the client's payments (the "Napojení plateb" step).
   * Present (and `true`) ONLY when the product said `launchpad_payments=1`;
   * absent otherwise, so every existing configuration reads byte-identically.
   */
  includesPaymentsSetup?: true;
}

export type ProductMetadataReading =
  /** No `launchpad` key at all: this product says nothing about Launchpad. */
  | { kind: "absent" }
  | { kind: "config"; config: StripeProductConfig }
  /** The product declared itself and we cannot read it. NEVER a default. */
  | { kind: "invalid"; issues: Array<MetadataIssueCode | PaymentsIssueCode> };

function text(metadata: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = metadata?.[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Read one Stripe product's metadata as a Launchpad purchase configuration.
 *
 * 🔴 Three decisions live in the shape of the answer:
 *
 * 1. **`absent` is not `invalid`.** A product with no `launchpad` key has
 *    stated nothing, so the purchase falls through to the legacy
 *    `stripe_product_mappings` row exactly as it did before F148 - that is what
 *    keeps every product mapped today working, and what makes a product nobody
 *    configured anywhere the same `no_match` it is now.
 * 2. **A `launchpad` key that is not `1` IS invalid.** An operator who wrote
 *    `launchpad: "true"` meant yes; letting that fall quietly back to the table
 *    (or to nothing) is the silent default this wave exists to remove. It is a
 *    named issue the console reads out.
 * 3. **`launchpad_journey` is the ONE field that may be missing or unknown.**
 *    It answers `full` through `journeyModeOf`, because that is the only safe
 *    direction (F143): a full client misread as `sales_only` would LOSE the
 *    eight steps of chapter 1 they still have to walk, while the reverse only
 *    shows steps their sections already report as untouched. Every other field
 *    decides something no later screen can correct for free.
 */
export function readLaunchpadProductMetadata(
  metadata: Record<string, unknown> | null | undefined
): ProductMetadataReading {
  const flagRaw = metadata?.[LAUNCHPAD_METADATA_KEYS.flag];
  if (flagRaw === undefined || flagRaw === null) return { kind: "absent" };
  const issues: Array<MetadataIssueCode | PaymentsIssueCode> = [];

  const flag = typeof flagRaw === "string" ? flagRaw.trim() : String(flagRaw);
  if (flag !== "1") issues.push("metadata_flag");

  const label = text(metadata, LAUNCHPAD_METADATA_KEYS.label);
  if (!label || label.length > LAUNCHPAD_LABEL_MAX) issues.push("metadata_label");

  const language = text(metadata, LAUNCHPAD_METADATA_KEYS.language);
  if (!language || !(LAUNCHPAD_LANGUAGES as readonly string[]).includes(language)) issues.push("metadata_language");

  const app = text(metadata, LAUNCHPAD_METADATA_KEYS.app);
  if (app !== "0" && app !== "1") issues.push("metadata_app");

  const source = text(metadata, LAUNCHPAD_METADATA_KEYS.source);
  if (!source || !(LAUNCHPAD_SOURCES as readonly string[]).includes(source)) issues.push("metadata_source");

  const start = text(metadata, LAUNCHPAD_METADATA_KEYS.start);
  if (!start || !(LAUNCHPAD_START_MODES as readonly string[]).includes(start)) issues.push("metadata_start");

  // F171: absent = no payment setup. PRESENT and unreadable is a named issue,
  // the `enableMobileApp` law (F153): the operator meant something, and reading
  // "no" over a value we could not read would sell the setup and not deliver it.
  const paymentsPresent = metadata?.[LAUNCHPAD_METADATA_KEYS.payments] !== undefined
    && metadata?.[LAUNCHPAD_METADATA_KEYS.payments] !== null;
  const payments = text(metadata, LAUNCHPAD_METADATA_KEYS.payments);
  if (paymentsPresent && payments !== "0" && payments !== "1") issues.push("metadata_payments");

  if (issues.length) return { kind: "invalid", issues };
  return {
    kind: "config",
    config: {
      label: label as string,
      marketLanguage: language as "cs" | "en",
      includesApp: app === "1",
      purchaseSource: source as "direct" | "upsell" | "webinar",
      instanceStartMode: start as "new" | "existing",
      // The only normalizing read in this module, and the reason is F143's.
      journeyMode: journeyModeOf(text(metadata, LAUNCHPAD_METADATA_KEYS.journey)),
      ...(payments === "1" ? { includesPaymentsSetup: true as const } : {})
    }
  };
}

// ============================================================
// F153: the productType dialect
// ============================================================

export const PRODUCT_TYPE_METADATA_KEYS = {
  archetype: "productType",
  language: "language",
  app: "enableMobileApp"
} as const;

export const PRODUCT_ARCHETYPES = ["start", "empire"] as const;
export type ProductArchetype = (typeof PRODUCT_ARCHETYPES)[number];

/**
 * What the client BOUGHT, as the unassigned queue and the audit print it
 * (F139's row law: the row says what they bought). These are product names, so
 * they are the same string in both markets and are never translated.
 */
export const PRODUCT_ARCHETYPE_LABELS: Readonly<Record<ProductArchetype, string>> = {
  start: "Start",
  empire: "Empire"
};

/**
 * F155b: is a RAW `productType` one of the two packages, and which one.
 *
 * 🔴 The value read here comes from the PLATFORM's licence record, not from a
 * Stripe product - two systems, one vocabulary, because Eliška owns both sides
 * and mirrored the platform's own two words into the Stripe metadata (F153).
 * That is why this reader lives beside the labels rather than as a second
 * two-word list in the console: a copy there would drift the day a third
 * package is named, and the console would silently stop highlighting it.
 *
 * Case is folded for the same reason the `productType` dialect folds it: these
 * two words are typed by hand on one side and stored by the platform on the
 * other, case is not information, and folding it cannot produce a DIFFERENT
 * package - only the same one. Anything unknown answers null, and the surface
 * then shows the platform's value exactly as it arrived.
 */
export function productArchetypeOf(value: string | null | undefined): ProductArchetype | null {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return (PRODUCT_ARCHETYPES as readonly string[]).includes(normalized)
    ? (normalized as ProductArchetype)
    : null;
}

/**
 * The issue codes of THIS dialect. They are deliberately a separate list from
 * `METADATA_ISSUE_CODES`: that one is the set a launchpad_* product can produce
 * and its own guard asserts exactly that set, so growing it here would make a
 * broken launchpad_* product owe issues it can never have. Both lists share the
 * `metadata_` prefix, which is what makes the console's "fix it at the PRODUCT"
 * next step (`importFixKey`) cover the new dialect without knowing about it.
 */
export const PRODUCT_TYPE_ISSUE_CODES = [
  "metadata_product_type",
  "metadata_product_language",
  "metadata_product_app",
  "metadata_dialects_disagree"
] as const;
export type ProductTypeIssueCode = (typeof PRODUCT_TYPE_ISSUE_CODES)[number];

export type ProductMetadataIssueCode = MetadataIssueCode | PaymentsIssueCode | ProductTypeIssueCode;

/** Every code a product's metadata can produce, for the readers that map ALL of them. */
export const PRODUCT_METADATA_ISSUE_CODES: readonly ProductMetadataIssueCode[] = [
  ...METADATA_ISSUE_CODES,
  ...PAYMENTS_ISSUE_CODES,
  ...PRODUCT_TYPE_ISSUE_CODES
];

export type ProductMetadataReadingAll =
  | { kind: "absent" }
  | { kind: "config"; config: StripeProductConfig; dialect: MetadataDialect }
  | { kind: "invalid"; issues: ProductMetadataIssueCode[] };

/** Which dialect decided a purchase. Rides the audit so "how many products still ride the old one" is a query. */
export type MetadataDialect = "launchpad" | "product_type";

/**
 * 🔴 Case is normalized for THIS dialect and only this one.
 *
 * The launchpad_* keys are compared byte for byte, and they stay that way: that
 * contract shipped, and loosening a shipped comparison is a behaviour change
 * this wave has no reason to make. These three keys are typed by hand into the
 * Stripe dashboard by the person who owns the products, their value sets are
 * tiny and disjoint under case folding, and "Empire" instead of "empire" is the
 * single likeliest way to write them wrong. Case is not information here, so
 * reading it is not a silent default - it cannot produce a DIFFERENT client,
 * only the same one.
 */
function enumValue(metadata: Record<string, unknown> | null | undefined, key: string): string | null {
  const raw = text(metadata, key);
  return raw === null ? null : raw.toLowerCase();
}

/**
 * Read one product's `productType` configuration.
 *
 * The three decisions in the shape of the answer:
 *
 * 1. **No `productType` key at all is `absent`**, exactly as `launchpad` is:
 *    this product says nothing about Launchpad through this dialect.
 * 2. **`language` missing or unreadable is a NAMED error and NOTHING is
 *    created** - the F148 law, because the market decides the client's whole
 *    dashboard, their e-mails and the language their materials are written in,
 *    and no screen afterwards can question it. There is no default: guessing
 *    Czech is precisely how an American client ends up reading Czech (F116).
 * 3. **`enableMobileApp` MISSING answers false**, because the app can be added
 *    later in the client editor while the opposite default would draw a client
 *    the three app steps they never bought. A `enableMobileApp` that is PRESENT
 *    and unreadable is still a named error: the operator meant something, and
 *    silently answering "no app" to a value we could not read would sell the
 *    app and not deliver it.
 */
export function readProductTypeMetadata(
  metadata: Record<string, unknown> | null | undefined
): { kind: "absent" } | { kind: "config"; config: StripeProductConfig } | { kind: "invalid"; issues: ProductTypeIssueCode[] } {
  const rawArchetype = metadata?.[PRODUCT_TYPE_METADATA_KEYS.archetype];
  if (rawArchetype === undefined || rawArchetype === null) return { kind: "absent" };
  const issues: ProductTypeIssueCode[] = [];

  const archetype = enumValue(metadata, PRODUCT_TYPE_METADATA_KEYS.archetype);
  if (!archetype || !(PRODUCT_ARCHETYPES as readonly string[]).includes(archetype)) issues.push("metadata_product_type");

  const language = enumValue(metadata, PRODUCT_TYPE_METADATA_KEYS.language);
  if (!language || !(LAUNCHPAD_LANGUAGES as readonly string[]).includes(language)) issues.push("metadata_product_language");

  // Absent is a real answer (false); present and unreadable is not. `1`/`0` are
  // accepted beside `true`/`false` because that is the alphabet the sibling
  // dialect already uses for the same fact, and an operator moving between the
  // two must not be punished for it.
  const appRaw = enumValue(metadata, PRODUCT_TYPE_METADATA_KEYS.app);
  const APP_TRUE = ["true", "1"];
  const APP_FALSE = ["false", "0"];
  const appPresent = metadata?.[PRODUCT_TYPE_METADATA_KEYS.app] !== undefined
    && metadata?.[PRODUCT_TYPE_METADATA_KEYS.app] !== null;
  if (appPresent && (appRaw === null || (!APP_TRUE.includes(appRaw) && !APP_FALSE.includes(appRaw)))) {
    issues.push("metadata_product_app");
  }

  if (issues.length) return { kind: "invalid", issues };
  return {
    kind: "config",
    config: expandArchetype(archetype as ProductArchetype, {
      marketLanguage: language as "cs" | "en",
      includesApp: appRaw !== null && APP_TRUE.includes(appRaw)
    })
  };
}

/**
 * The archetype expands into the SAME config shape the launchpad_* dialect
 * states field by field. Eliška's own words decide the three fields she does
 * not write: "oba jedou celou cestu (příprava Buddyho i prodej), ne jen
 * prodejní část" - so `full` for both, and a purchase through this channel is
 * `direct` with a `new` instance, which is what every purchase has been.
 *
 * A product that needs anything else says so in the launchpad_* dialect, which
 * is the whole reason that one is not going away.
 */
function expandArchetype(
  archetype: ProductArchetype,
  input: { marketLanguage: "cs" | "en"; includesApp: boolean }
): StripeProductConfig {
  return {
    label: PRODUCT_ARCHETYPE_LABELS[archetype],
    marketLanguage: input.marketLanguage,
    includesApp: input.includesApp,
    purchaseSource: "direct",
    instanceStartMode: "new",
    journeyMode: "full",
    productArchetype: archetype
  };
}

/**
 * The fields a disagreement between the two dialects is measured on: everything
 * that DECIDES something about the client. The label is deliberately not here -
 * it is what the queue prints, not what the client gets, so two dialects naming
 * the same purchase differently ("DFY balíček" and "Empire") is not a
 * contradiction about anything, and the launchpad_* label wins because a human
 * wrote it about this product.
 */
function decidedFields(config: StripeProductConfig): string {
  return [
    config.marketLanguage,
    config.includesApp ? "app" : "no_app",
    config.purchaseSource,
    config.instanceStartMode,
    config.journeyMode
  ].join("|");
}

/**
 * 🔴 THE ONE READING of a Stripe product's metadata, over BOTH dialects.
 *
 * The decision F153 had to make and this function IS: what happens when one
 * product carries both.
 *
 * - Only one dialect declared itself: that one answers, unchanged.
 * - Both declared themselves and AGREE on every deciding field: the
 *   launchpad_* config wins. It is the more explicit statement (it names each
 *   field instead of expanding an archetype) and it carries the operator's own
 *   label, and since the two agree there is nothing to lose by preferring it.
 *   The archetype rides along, because it is a fact the launchpad_* dialect
 *   cannot state and the welcome mail needs it.
 * - Both declared themselves and DISAGREE about something that decides the
 *   client: a NAMED error, and nothing is created. This is the F148 law
 *   applied one level up. A product that declares itself twice with two
 *   different answers is a product we cannot read, and picking a winner
 *   silently is exactly the invisible-default this family of waves exists to
 *   remove: the wrong market or a missing mobile app is not visible on any
 *   screen afterwards, and nobody would ever go looking.
 * - Either dialect declared itself and is BROKEN: invalid, with the issues of
 *   both merged. A readable second dialect does not rescue a broken first one,
 *   for the same reason: the operator wrote something they meant, and reading
 *   past it is the silent default again.
 */
export function readProductMetadata(
  metadata: Record<string, unknown> | null | undefined
): ProductMetadataReadingAll {
  const launchpad = readLaunchpadProductMetadata(metadata);
  const productType = readProductTypeMetadata(metadata);

  if (launchpad.kind === "absent" && productType.kind === "absent") return { kind: "absent" };

  const issues: ProductMetadataIssueCode[] = [];
  if (launchpad.kind === "invalid") issues.push(...launchpad.issues);
  if (productType.kind === "invalid") issues.push(...productType.issues);
  if (issues.length) return { kind: "invalid", issues };

  if (launchpad.kind === "config" && productType.kind === "config") {
    if (decidedFields(launchpad.config) !== decidedFields(productType.config)) {
      return { kind: "invalid", issues: ["metadata_dialects_disagree"] };
    }
    return {
      kind: "config",
      config: { ...launchpad.config, productArchetype: productType.config.productArchetype },
      dialect: "launchpad"
    };
  }
  if (launchpad.kind === "config") return { kind: "config", config: launchpad.config, dialect: "launchpad" };
  if (productType.kind === "config") return { kind: "config", config: productType.config, dialect: "product_type" };
  return { kind: "absent" };
}
